import { useEffect, useState, type ReactNode } from 'react';
import { Navigate } from 'react-router-dom';

import { PageLoader } from '@knitknot/shared/components/ui/page-loader';
import { ErrorState } from '@knitknot/shared/components/ui/error-state';

import { useEntitlements } from '@/hooks/api/useEntitlements';
import { useWorkspaceStore } from '@/hooks/state';
import { consoleRoutes } from '@/lib/routes';

/**
 * The lane gate for every Content › Updates route.
 *
 * §9.4's recorded rule: **gated (flag off) = invisible.** Not a locked page,
 * not an upsell — the destination simply is not there, and a direct URL lands
 * on the workspace's ungated Content work. The routers enforce the same flag with a
 * 404, so this only decides what renders; it is never the authorization.
 *
 * The flag is fail-closed while loading, so the loader (not the page) is what
 * shows until we know.
 */
export function OptimizationGate({ children }: { children: ReactNode }) {
  const workspaceId = useWorkspaceStore((s) => s.workspaceId) ?? undefined;
  const { managedOptimizationEnabled, isLoading, isError, retry } = useEntitlements(workspaceId);
  const [confirmingWorkspace, setConfirmingWorkspace] = useState<string | null>(null);
  const [confirmedDisabledWorkspace, setConfirmedDisabledWorkspace] = useState<string | null>(null);

  // A fail-closed flag read returns `false`, including when its backing store is
  // briefly unavailable. Confirm a negative once before replacing a deep link:
  // the gated body never renders during confirmation, while a transient cold-
  // boot miss does not strand an enabled workspace outside Optimize.
  useEffect(() => {
    if (
      !workspaceId ||
      isLoading ||
      isError ||
      managedOptimizationEnabled ||
      confirmingWorkspace === workspaceId ||
      confirmedDisabledWorkspace === workspaceId
    ) {
      return;
    }

    setConfirmingWorkspace(workspaceId);
    void retry().finally(() => {
      setConfirmedDisabledWorkspace(workspaceId);
      setConfirmingWorkspace(null);
    });
  }, [
    confirmedDisabledWorkspace,
    confirmingWorkspace,
    isError,
    isLoading,
    managedOptimizationEnabled,
    retry,
    workspaceId,
  ]);

  if (!workspaceId || isLoading || confirmingWorkspace === workspaceId) return <PageLoader />;
  if (isError) {
    return (
      <ErrorState
        title="Couldn't check Updates access"
        description="The workspace capability didn't load. Try again without leaving this page."
        onRetry={retry}
      />
    );
  }
  if (!managedOptimizationEnabled) {
    if (confirmedDisabledWorkspace !== workspaceId) return <PageLoader />;
    return <Navigate to={consoleRoutes.publish.playbooks} replace />;
  }
  return <>{children}</>;
}

export default OptimizationGate;
